import type {
  QuickBooksCashPlanningResponse,
  QuickBooksCashPlanningRow,
  QuickBooksCashPlanningWeekSummary,
} from '../../../data/ServerApiClient'
import {
  signedCashAmount,
  type CashTableCategoryKey,
} from './cfoCashTableModel'

export type CashPlanningMove = {
  expectationId: string | null
  rowKey: CashTableCategoryKey
  scenarioKey: string
  eventType: string
  counterparty: string | null
  title: string
  /** The identity the expectation claims: a QBO document id for a document row,
   * the recurring `stream_key` for a recurring one, null when there is nothing
   * to replace. */
  documentId: string | null
  /** The moved ROW's own source_type. The expectation's source_type must be
   * derived from this, never from the treasurer row — see
   * `expectationSourceTypeFor`. */
  sourceType: string | null
  /** The moved row's provenance class, so a recurring stream claims by
   * stream_key rather than falling through to a non-claiming type. */
  provenance?: string | null
  fromDate: string
  targetDate: string
  amount: number
}

/**
 * The `source_type` to persist on an expectation that re-times a document.
 *
 * This MUST come from the moved row's own source_type. Deriving it from the
 * treasurer row (`rowKey === 'collections' ? 'ar_invoice' : 'planned_cash_item'`)
 * stamped every outflow as `planned_cash_item`, which contains none of
 * `invoice` / `ar` / `bill` — the substrings the server's
 * `suppress_ml_rows_covered_by_expectations` requires — so deferring a bill
 * left the ML legs in place and counted the outflow twice.
 */
export const expectationSourceTypeFor = (
  rowSourceType: string | null | undefined,
  rowKey: CashTableCategoryKey,
  provenance?: string | null,
): string => {
  const source = String(rowSourceType || '').trim().toLowerCase()
  if (source === 'invoice' || source === 'ar_invoice') return 'ar_invoice'
  if (source === 'bill' || source === 'ap_bill') return 'ap_bill'
  // A detected recurring stream (payroll, rent, insurance) has no document, but
  // its stream_key is a date-free identity the server can claim one occurrence
  // of — see `cash_claim_from_expectation`.
  if (provenance === 'recurring' || source === 'recurring_stream_schedule') {
    return 'recurring_stream'
  }
  // No document and no stream behind the row: nothing to suppress, so the
  // expectation is additive by design and must not claim what it cannot replace.
  return rowKey === 'collections' ? 'ar_invoice' : 'planned_cash_item'
}

const roundMoney = (value: number): string => value.toFixed(2)

const isoDate = (value: unknown): string => String(value || '').slice(0, 10)

const epochDay = (value: unknown): number | null => {
  const [year, month, day] = isoDate(value).split('-').map(Number)
  if (!year || !month || !day) return null
  return Math.floor(Date.UTC(year, month - 1, day) / 86_400_000)
}

const shiftDateDays = (value: string, days: number): string => {
  const day = epochDay(value)
  if (day == null) return value
  return new Date((day + days) * 86_400_000).toISOString().slice(0, 10)
}

const mondayFor = (value: string): string => {
  const day = epochDay(value)
  if (day == null) return value
  const date = new Date(day * 86_400_000)
  const daysFromMonday = (date.getUTCDay() + 6) % 7
  return shiftDateDays(value, -daysFromMonday)
}

const effectiveDate = (row: QuickBooksCashPlanningRow): string =>
  isoDate(row.expected_date || row.week_start)

const isSummaryRow = (row: QuickBooksCashPlanningRow): boolean =>
  String(row.source_type || '').toLowerCase() === 'cash_week_summary' ||
  String(row.event_type || '').toLowerCase() === 'net_operating_cash'

const isPostedActual = (row: QuickBooksCashPlanningRow): boolean =>
  row.row_kind === 'actual' ||
  String(row.source_type || '').toLowerCase() === 'posted_cash_transaction'

const documentIdFor = (row: QuickBooksCashPlanningRow): string | null => {
  const key = String(row.source_key || '').trim()
  if (!key) return null
  const segments = key.split(':')
  const id = String(segments.length >= 2 ? segments[1] : segments[0]).trim()
  return id && id.toLowerCase() !== 'manual' ? id.toLowerCase() : null
}

const expectationCanSuppressDocument = (
  row: QuickBooksCashPlanningRow,
): boolean => {
  const source = String(row.source_type || '').toLowerCase()
  return (
    row.row_kind === 'expectation' &&
    !source.includes('recurring') &&
    (source.includes('invoice') ||
      source.includes('ar') ||
      source.includes('bill'))
  )
}

/** Mirrors the server's HALF-cadence tolerance in `recurring_claim_tolerance_days`. */
const RECURRING_TOLERANCE_DAYS: Record<string, number> = {
  weekly: 3,
  biweekly: 6,
  semimonthly: 7,
  monthly: 14,
}

const evidenceValue = (
  row: QuickBooksCashPlanningRow,
  key: string,
): string | null => {
  const evidence = row.evidence_json
  if (!Array.isArray(evidence)) return null
  for (const item of evidence) {
    if (item && typeof item === 'object' && key in item) {
      const value = (item as Record<string, unknown>)[key]
      if (typeof value === 'string' && value.trim()) return value.trim()
    }
  }
  return null
}

/**
 * The recurring occurrence an expectation claims, mirroring the server's
 * `cash_claim_from_expectation`.
 *
 * Anchors on the ORIGINAL date carried in `source_payload_json.claim.occurrence`,
 * not the expectation's own (target) date — otherwise a move longer than half a
 * cadence would miss the row it replaced and suppress a different occurrence.
 */
const recurringClaimFor = (
  row: QuickBooksCashPlanningRow,
): { streamKey: string; occurrence: string } | null => {
  if (row.row_kind !== 'expectation') return null
  if (!String(row.source_type || '').toLowerCase().includes('recurring')) return null
  const streamKey = documentIdFor(row)
  // NO fallback to the expectation's own date. That date is where the user moved
  // the row TO, and matching on it drops whichever occurrence happens to sit
  // near the target — deleting a payment the user never touched. Without an
  // explicit claim, suppress nothing: a transient double-count that the refetch
  // corrects is strictly safer than removing the wrong row.
  const occurrence = isoDate(row.source_payload_json?.claim?.occurrence)
  return streamKey && occurrence ? { streamKey, occurrence } : null
}

const rowFallsInWeek = (
  row: QuickBooksCashPlanningRow,
  week: QuickBooksCashPlanningWeekSummary,
): boolean => {
  const day = epochDay(effectiveDate(row))
  const start = epochDay(week.bucket_start || week.week_start)
  const end = epochDay(
    week.bucket_end || shiftDateDays(isoDate(week.week_start), 6),
  )
  return day != null && start != null && end != null && day >= start && day <= end
}

const recalculateWeeks = (
  weeks: QuickBooksCashPlanningWeekSummary[],
  rows: QuickBooksCashPlanningRow[],
): QuickBooksCashPlanningWeekSummary[] => {
  let openingCash: number | null = null
  return weeks.map((week) => {
    if (week.week_status === 'closed') return week
    if (openingCash == null) {
      const parsed = Number(week.opening_cash)
      openingCash = Number.isFinite(parsed) ? parsed : 0
    }
    const weekRows = rows.filter(
      (row) =>
        !isPostedActual(row) && !isSummaryRow(row) && rowFallsInWeek(row, week),
    )
    let receipts = 0
    let disbursements = 0
    let bridge = 0
    for (const row of weekRows) {
      const signed = signedCashAmount(row)
      if (row.row_kind === 'bridge') bridge += signed
      else if (signed >= 0) receipts += signed
      else disbursements += signed
    }
    const net = receipts + disbursements + bridge
    const endingCash = openingCash + net
    const next = {
      ...week,
      opening_cash: roundMoney(openingCash),
      receipts: roundMoney(receipts),
      disbursements: roundMoney(disbursements),
      bridge_amount: roundMoney(bridge),
      net_cash_change: roundMoney(net),
      ending_cash: roundMoney(endingCash),
      rollup_json: {
        ...(week.rollup_json || {}),
        totals: {
          receipts: roundMoney(receipts),
          disbursements: roundMoney(disbursements),
          bridge: roundMoney(bridge),
        },
      },
      lineage_json: {
        ...(week.lineage_json || {}),
        row_count: weekRows.length,
        local_projection: true,
      },
    }
    openingCash = endingCash
    return next
  })
}

// A move or a drill always targets one of the nine buckets — never a
// computed subtotal — so this stays exhaustive over the category keys.
const rowLabelFor = (rowKey: CashTableCategoryKey): string =>
  ({
    beginning: 'Beginning cash',
    collections: 'Collections',
    other_inflows: 'Other inflows',
    payroll: 'Payroll',
    rent: 'Rent',
    insurance: 'Insurance',
    vendors_ap: 'Vendors / AP',
    debt: 'Debt',
    taxes: 'Taxes',
    other: 'Other',
    net: 'Net cash change',
    ending: 'Ending cash',
  })[rowKey]

export const optimisticExpectationRow = (
  response: QuickBooksCashPlanningResponse,
  move: CashPlanningMove,
  optimisticId: string,
): QuickBooksCashPlanningRow => {
  const existing = move.expectationId
    ? response.expectations.find(
        (row) => row.expectation_id === move.expectationId,
      )
    : null
  const sourceType =
    existing?.source_type ||
    expectationSourceTypeFor(move.sourceType, move.rowKey, move.provenance)
  const id = optimisticId
  return {
    ...(existing || ({} as QuickBooksCashPlanningRow)),
    id,
    row_kind: 'expectation',
    package_id: null,
    expectation_id: id,
    scenario_key: move.scenarioKey,
    week_start: mondayFor(move.targetDate),
    expected_date: move.targetDate,
    metric_key: move.amount >= 0 ? 'receipts' : 'disbursements',
    category_key: existing?.category_key || 'user_expectations',
    category_label: existing?.category_label || 'User expectations',
    cash_table_row_key: move.rowKey,
    cash_table_row_label: rowLabelFor(move.rowKey),
    event_type: move.eventType,
    label: existing?.label || `${move.title} — moved from ${move.fromDate}`,
    counterparty_name: move.counterparty,
    expected_amount: roundMoney(Math.abs(move.amount)),
    low_amount: existing?.low_amount || null,
    high_amount: existing?.high_amount || null,
    confidence: existing?.confidence ?? null,
    forecast_basis: 'User-reviewed cash expectation',
    assumption_summary: existing?.assumption_summary || null,
    review_priority:
      Math.abs(move.amount) >= 10_000 ? 'high' : 'normal',
    editable: true,
    edit_target_type: 'expectation',
    edit_target_id: id,
    source_type: sourceType,
    source_key:
      existing?.source_key ||
      `${sourceType}:${move.documentId || 'manual'}:${mondayFor(move.targetDate)}:${move.eventType}`,
    feedback_state: 'pending_write',
    // Only the OPTIMISTIC row carries this: the server applies the claim before
    // it serializes, so a refetched payload has already had the claimed row
    // removed and never needs to re-derive it.
    source_payload_json:
      move.provenance === 'recurring' && move.fromDate
        ? { claim: { occurrence: move.fromDate } }
        : (existing?.source_payload_json ?? null),
    material: Math.abs(move.amount) >= 10_000,
    evidence_json: existing?.evidence_json || [],
    warning_json: existing?.warning_json || [],
  }
}

export const projectCashPlanningExpectation = (
  response: QuickBooksCashPlanningResponse,
  expectation: QuickBooksCashPlanningRow,
  replaceExpectationId?: string | null,
): QuickBooksCashPlanningResponse => {
  const replaceIds = new Set(
    [replaceExpectationId, expectation.expectation_id, expectation.id].filter(
      Boolean,
    ) as string[],
  )
  const coveredDocument = expectationCanSuppressDocument(expectation)
    ? documentIdFor(expectation)
    : null
  const coveredRecurring = recurringClaimFor(expectation)
  const retained = response.rows.filter((row) => {
    if (
      row.row_kind === 'expectation' &&
      (replaceIds.has(row.id) ||
        (row.expectation_id != null && replaceIds.has(row.expectation_id)))
    ) {
      return false
    }
    if (row.row_kind === 'expectation' || row.row_kind === 'bridge') return true
    if (coveredRecurring) {
      const streamKey = evidenceValue(row, 'stream_key')
      if (streamKey === coveredRecurring.streamKey) {
        const cadence = (evidenceValue(row, 'stream_cadence') || '').toLowerCase()
        const tolerance = RECURRING_TOLERANCE_DAYS[cadence] ?? 14
        const rowDay = epochDay(effectiveDate(row))
        const claimDay = epochDay(coveredRecurring.occurrence)
        if (rowDay != null && claimDay != null && Math.abs(rowDay - claimDay) <= tolerance) {
          return false
        }
      }
    }
    if (!coveredDocument) return true
    return documentIdFor(row) !== coveredDocument
  })
  retained.push(expectation)
  // The plan is the truth; the monthly forecast is a comparison.
  //
  // This used to re-derive a per-month "bridge" plug so every month's total
  // stayed pinned to its pre-edit value — which silently CANCELLED the user's
  // move at month grain and parked the offset on the 1st. It also diverged from
  // the server, which emits no bridge rows at all: `build_bridge_rows` reads
  // cash_flow rows from `quickbooks_fpa_ml_forecast_line_metadata`, and that
  // table only ever holds profit_and_loss and balance_sheet lines. So an
  // optimistic cross-month move appeared to cancel itself and then jumped when
  // the refetch landed. Bridges the server DID send are preserved untouched.
  const rows = retained.sort((left, right) =>
    effectiveDate(left).localeCompare(effectiveDate(right)) ||
    left.row_kind.localeCompare(right.row_kind) ||
    left.label.localeCompare(right.label),
  )
  const expectations = response.expectations
    .filter(
      (row) =>
        !replaceIds.has(row.id) &&
        !(row.expectation_id != null && replaceIds.has(row.expectation_id)),
    )
    .concat(expectation)
    .sort((left, right) => effectiveDate(left).localeCompare(effectiveDate(right)))
  return {
    ...response,
    rows,
    expectations,
    bridge_rows: response.bridge_rows,
    weeks: recalculateWeeks(response.weeks, rows),
    contract: {
      ...response.contract,
      row_count: rows.length + response.source_rows.length,
    },
  }
}

export const reconcileCashPlanningExpectation = (
  response: QuickBooksCashPlanningResponse,
  optimisticId: string,
  persisted: QuickBooksCashPlanningRow,
): QuickBooksCashPlanningResponse =>
  projectCashPlanningExpectation(response, persisted, optimisticId)
